import classNames from "classnames";

import { Privilege } from "services/rbac/types";

import { rolesPrivileges, usersPrivileges } from "pages/govern/privileges";

import { CreateMenu } from "components/LeftSidebar/CreateMenu/CreateMenu";

import { useCurrentAccount } from "../../Account/useCurrentAccount";
import { useAccessManager } from "../../App/accessManager";
import { LeftSidebarSection } from "../types";

import styles from "./Header.module.scss";

type isAvailableFunction = (context: {
  isAllowedTo: ReturnType<typeof useAccessManager>["isAllowedTo"];
  accountName: string;
}) => boolean;

const SECTIONS: {
  [key in LeftSidebarSection]: {
    title: string;
    testId: string;
    isAvailable: isAvailableFunction;
  };
} = {
  [LeftSidebarSection.DATA]: {
    title: "Data",
    testId: "explore-section-data",
    isAvailable: () => true,
  },
  [LeftSidebarSection.COMPUTE]: {
    title: "Compute",
    testId: "explore-section-compute",
    isAvailable: () => !process.env.REACT_APP_LITE,
  },
  [LeftSidebarSection.SECURITY]: {
    title: "Security",
    testId: "explore-section-security",
    isAvailable: context => {
      const { isAllowedTo, accountName } = context;
      const privilegeMatch = (privilege: Privilege) =>
        isAllowedTo(
          privilege.objectType,
          privilege.objectName,
          privilege.privilegeType
        );

      return (
        // as for Govern -> Users
        usersPrivileges(accountName).some(privilegeMatch) ||
        // as for Govern -> Roles
        rolesPrivileges(accountName).some(privilegeMatch)
      );
    },
  },
};

type Props = {
  activeSection: LeftSidebarSection;
  setActiveSection: (section: LeftSidebarSection) => void;
};

export const Header = (props: Props) => {
  const { activeSection, setActiveSection } = props;

  const { isAllowedTo } = useAccessManager();
  const { getAccount } = useCurrentAccount();
  const isLite = Boolean(process.env.REACT_APP_LITE);

  const accountName = getAccount().accountName;

  return (
    <div
      className={styles.header}
      data-testid="object-exporer-header"
    >
      {Object.entries(SECTIONS)
        .filter(([_, section]) => {
          return section.isAvailable({
            isAllowedTo,
            accountName,
          });
        })
        .map(([key, section]) => {
          const { title, testId } = section;
          return (
            <div
              key={testId}
              onClick={() => {
                setActiveSection(key as LeftSidebarSection);
              }}
              className={classNames(styles.tab, {
                [styles.active]: key === activeSection,
              })}
              data-testid={testId}
            >
              {title}
            </div>
          );
        })}

      {!isLite && <CreateMenu />}
    </div>
  );
};
